import { memo, useEffect } from 'react';
import { Box, Card, Fade, Typography } from '@mui/material';

import { TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchTitleMetadata } from '@/redux/slices/titleV2Slice';
import { selectTitleLoading, selectFilteredActiveTitle } from '@/redux/selectors/titleSelectors';
import { selectActiveTitle, selectChildrenByTitleId, selectIsActiveTitleLoading, selectTitleMetadataById } from '@/redux/selectors/titleV2Selectors';
import { selectSelectedHierarchy } from '@/redux/selectors/orderPlacementSelectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { isTitleEntityV2 } from '@/_utils/utils';
import { Spinner } from '@/components/_common';
import TitleBrowserItemV2 from '@/components/OrderPlacement/OPStep2FileSelection/v2/TitleBrowserV2/TitleBrowserItemV2';
import TitleBrowserEntity from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserEntity';
import TitleBrowserItem from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserItem';

const TitleBrowser = () => {
  const dispatch = useAppDispatch();
  const isTitleV2 = useConfigValue(['titleConfig', 'v2']);
  const displayTitleMetadata = useConfigValue(['orderPlacementConfig', 'displayTitleMetadataByhierarchy']);
  const activeTitle = isTitleV2 ? useAppSelector(selectActiveTitle) : useAppSelector(selectFilteredActiveTitle);
  const titleChildren = useAppSelector((state) => selectChildrenByTitleId(state, activeTitle?.id || 0));
  const selectedHierarchy = useAppSelector(selectSelectedHierarchy);
  const activeTitleChildren = activeTitle ? (isTitleEntityV2(activeTitle) ? titleChildren : activeTitle?.children) : undefined;
  const isLoading = isTitleV2 ? useAppSelector(selectTitleLoading) : useAppSelector(selectIsActiveTitleLoading);
  // if every child is a version, we don't want to show the parent because we must have clicked on an episode
  const isEntity = activeTitleChildren?.every((c) => c.entity_type === 'version');
  const metadata = useAppSelector((state) => selectTitleMetadataById(state, activeTitle?.id));

  useEffect(() => {
    if (!displayTitleMetadata || !activeTitle) return;

    const { entity_type, id } = activeTitle;
    if (entity_type === 'season' || entity_type === 'series') return;

    dispatch(
      fetchTitleMetadata({
        body: {
          language: 'en-US',
          hierarchy: selectedHierarchy,
          entity_ids: [id]
        }
      })
    );
  }, [displayTitleMetadata, activeTitle?.id, selectedHierarchy]);

  if (isLoading)
    return (
      <Fade in timeout={400}>
        <Card elevation={0} sx={{ mt: 2, py: 5 }}>
          <Box mt={8}>
            <Spinner contained />
            <Typography variant="subtitle2" mt={3} align="center" sx={{ opacity: 0.8 }}>
              Loading Title
            </Typography>
          </Box>
        </Card>
      </Fade>
    );

  if (!activeTitle || !activeTitleChildren) return null;

  if (isEntity) {
    return <TitleBrowserEntity defaultExpanded={activeTitleChildren.length > 0} entity={activeTitle} metadata={metadata} />;
  }

  return (
    <Box>
      {isTitleEntityV2(activeTitle)
        ? (activeTitleChildren as TitleEntityV2[]).map((child) => <TitleBrowserItemV2 key={child.id} level={2} data={child} />)
        : (activeTitleChildren as TitleEntity[])
            .filter((c) => c.entity_type !== 'version')
            .map((child) => <TitleBrowserItem key={child.id} level={2} data={child} />)}
    </Box>
  );
};

export default memo(TitleBrowser);
